import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Zap, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { cashfree: Zap, razorpay: CreditCard };

const AdminPayments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payments/gateways`, { headers });
      setData(r.data);
    } catch (e) {
      toast({ title: 'Failed to load gateways', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const setActive = async (gateway) => {
    setSaving(gateway);
    try {
      await axios.post(`${API}/admin/payments/gateway/active`, { gateway }, { headers });
      toast({ title: 'Active gateway updated', description: `All wallet top-ups will now use ${gateway}.` });
      load();
    } catch (e) {
      toast({
        title: 'Failed to switch',
        description: e.response?.data?.detail || 'Check that the gateway keys are configured.',
        variant: 'destructive',
      });
    } finally {
      setSaving('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-payments-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Payment Gateway</h2>
        <p className="text-sm text-gray-600 mt-1">
          Pick one active gateway — all PG-owner wallet top-ups will route through it. Works like the LLM key switcher.
        </p>
      </div>

      <Card className="p-4 mb-6 border-l-4 border-l-blue-500 bg-blue-50/50">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm text-blue-900">Currently active: <span className="uppercase">{data?.active}</span></p>
            <p className="text-xs text-blue-800 mt-0.5">
              Switching here is instant. Users will see the new gateway the next time they open the Top-Up modal. Manual UPI is always available as a fallback.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.gateways?.map(gw => {
          const Icon = ICONS[gw.key] || CreditCard;
          const isActive = data.active === gw.key;
          return (
            <Card
              key={gw.key}
              className={`p-5 transition-all ${isActive ? 'ring-2 ring-emerald-500 border-emerald-400' : 'hover:border-gray-400'}`}
              data-testid={`gw-card-${gw.key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                    <Icon size={20} className={isActive ? 'text-emerald-700' : 'text-gray-600'} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{gw.label}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{gw.environment}</p>
                  </div>
                </div>
                {isActive ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                    <CheckCircle2 size={12} /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    <Circle size={10} /> Inactive
                  </span>
                )}
              </div>

              <div className="space-y-1.5 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Configured</span>
                  <span className={gw.configured ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
                    {gw.configured ? 'Yes' : 'Missing keys'}
                  </span>
                </div>
                {gw.client_id_masked && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client ID</span>
                    <span className="font-mono text-xs">{gw.client_id_masked}</span>
                  </div>
                )}
              </div>

              {!isActive && (
                <Button
                  onClick={() => setActive(gw.key)}
                  disabled={!gw.configured || saving === gw.key}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`gw-activate-${gw.key}`}
                >
                  {saving === gw.key ? 'Switching…' : `Activate ${gw.label}`}
                </Button>
              )}
              {isActive && (
                <div className="text-center py-2 text-sm text-emerald-700 font-medium">
                  All new top-ups route through {gw.label}
                </div>
              )}
              {!gw.configured && (
                <p className="text-xs text-red-600 mt-2 flex items-start gap-1">
                  <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                  Add {gw.key === 'cashfree' ? 'CASHFREE_CLIENT_ID & CASHFREE_CLIENT_SECRET' : 'RAZORPAY_KEY_ID & RAZORPAY_KEY_SECRET'} to backend .env to activate.
                </p>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminPayments;
